const E164_PHONE_REGEX = /^\+[1-9]\d{1,14}$/
const STANDARD_ATTRIBUTE_KEYS = ['email', 'email_subscribe', 'name', 'firstName', 'lastName', 'first_name', 'last_name', 'country', 'phone']

function getBrazeSdk (brazeClient) {
	if (!brazeClient || brazeClient.isReady === false) {
		return null
	}
	return brazeClient.sdk || brazeClient
}

function setBrazeUserName (user, attributes) {
	const fullName = attributes.name || ''
	const firstName = attributes.firstName || attributes.first_name || fullName.split(' ')[0]
	const lastName = attributes.lastName || attributes.last_name || fullName.split(' ').slice(1).join(' ')
	if (firstName && typeof user.setFirstName === 'function') {
		user.setFirstName(firstName)
	}
	if (lastName && typeof user.setLastName === 'function') {
		user.setLastName(lastName)
	}
}

function getEmailSubscriptionValue (sdk, value) {
	const types = sdk && sdk.User && sdk.User.NotificationSubscriptionTypes
	if (value === 'opted_in') {
		return (types && types.OPTED_IN) || 'opted_in'
	}
	if (value === 'subscribed') {
		return (types && types.SUBSCRIBED) || 'subscribed'
	}
	if (value === 'unsubscribed') {
		return (types && types.UNSUBSCRIBED) || 'unsubscribed'
	}
	return null
}

function setBrazeUserAttributes (user, attributes = {}, sdk = null) {
	if (!user) {
		return
	}
	if (attributes.email && typeof user.setEmail === 'function') {
		user.setEmail(attributes.email)
	}
	const subscriptionValue = getEmailSubscriptionValue(sdk, attributes.email_subscribe)
	if (subscriptionValue !== null && typeof user.setEmailNotificationSubscriptionType === 'function') {
		user.setEmailNotificationSubscriptionType(subscriptionValue)
	}
	if (attributes.name || attributes.firstName || attributes.lastName || attributes.first_name || attributes.last_name) {
		setBrazeUserName(user, attributes)
	}
	if (attributes.country && typeof user.setCountry === 'function') {
		user.setCountry(attributes.country)
	}
	if (attributes.phone && E164_PHONE_REGEX.test(attributes.phone) && typeof user.setPhoneNumber === 'function') {
		user.setPhoneNumber(attributes.phone)
	}
	Object.keys(attributes).forEach((key) => {
		const value = attributes[key]
		if (!STANDARD_ATTRIBUTE_KEYS.includes(key) && value !== undefined && value !== null && typeof user.setCustomUserAttribute === 'function') {
			user.setCustomUserAttribute(key, value)
		}
	})
}

function createDisabledBrazeClient () {
	return {
		isReady: false,
		sdk: null
	}
}

function createReadyBrazeClient (sdk) {
	return {
		isReady: true,
		sdk
	}
}

function isPlainObject (value) {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function attributeValuesEqual (a, b) {
	if (a === b) {
		return true
	}
	if (isPlainObject(a) && isPlainObject(b)) {
		try {
			return JSON.stringify(a) === JSON.stringify(b)
		} catch (e) {
			return false
		}
	}
	if (Array.isArray(a) && Array.isArray(b)) {
		try {
			return JSON.stringify(a) === JSON.stringify(b)
		} catch (e) {
			return false
		}
	}
	return false
}

function getChangedAttributes (previousAttributes, nextAttributes) {
	const changed = {}
	Object.keys(nextAttributes).forEach((key) => {
		const nextValue = nextAttributes[key]
		if (nextValue === undefined || nextValue === null) {
			return
		}
		if (!attributeValuesEqual(previousAttributes[key], nextValue)) {
			changed[key] = nextValue
		}
	})
	return changed
}

function identifyBrazeUser (brazeClient, userId, attributes = {}, logger = console) {
	const sdk = getBrazeSdk(brazeClient)
	if (!sdk || !userId || typeof sdk.changeUser !== 'function') {
		return false
	}
	try {
		const cache = (brazeClient && brazeClient._identityCache) || { userId: null, attributes: {} }
		const isNewUser = cache.userId !== userId
		if (isNewUser) {
			sdk.changeUser(userId)
			cache.userId = userId
			cache.attributes = {}
		}
		const changedAttributes = getChangedAttributes(cache.attributes, attributes)
		const hasChanges = Object.keys(changedAttributes).length > 0
		if (hasChanges) {
			setBrazeUserAttributes(typeof sdk.getUser === 'function' ? sdk.getUser() : null, changedAttributes, sdk)
			Object.keys(changedAttributes).forEach((key) => {
				cache.attributes[key] = changedAttributes[key]
			})
		}
		if (brazeClient) {
			brazeClient._identityCache = cache
		}
		if (typeof sdk.openSession === 'function') {
			sdk.openSession()
		}
		if ((isNewUser || hasChanges) && typeof sdk.requestImmediateDataFlush === 'function') {
			sdk.requestImmediateDataFlush()
		}
		return true
	} catch (error) {
		logger.warn('Failed to identify Braze user', error)
		return false
	}
}

function trackBrazeEvent (brazeClient, event, data = {}, logger = console) {
	const sdk = getBrazeSdk(brazeClient)
	if (!sdk || !event || typeof sdk.logCustomEvent !== 'function') {
		return false
	}
	try {
		sdk.logCustomEvent(event, data)
		return true
	} catch (error) {
		logger.warn('Failed to track Braze event', error)
		return false
	}
}

function resetBrazeUser (brazeClient, logger = console) {
	const sdk = getBrazeSdk(brazeClient)
	if (!sdk) {
		return false
	}
	try {
		if (brazeClient) {
			brazeClient._identityCache = null
		}
		if (typeof sdk.wipeData === 'function') {
			sdk.wipeData()
			return true
		}
		if (typeof sdk.requestImmediateDataFlush === 'function') {
			sdk.requestImmediateDataFlush()
			return true
		}
		return false
	} catch (error) {
		logger.warn('Failed to reset Braze user', error)
		return false
	}
}

function isBrazePushSupported (brazeClient) {
	const sdk = getBrazeSdk(brazeClient)
	if (!sdk || typeof sdk.isPushSupported !== 'function') {
		return false
	}
	return Boolean(sdk.isPushSupported())
}

function requestBrazePushPermission (brazeClient, callbacks = {}, logger = console) {
	const sdk = getBrazeSdk(brazeClient)
	if (!sdk || !isBrazePushSupported(brazeClient) || typeof sdk.requestPushPermission !== 'function') {
		return false
	}
	try {
		sdk.requestPushPermission(
			() => callbacks.onGranted && callbacks.onGranted(),
			temporary => callbacks.onDenied && callbacks.onDenied(temporary)
		)
		return true
	} catch (error) {
		logger.warn('Failed to request Braze push permission', error)
		return false
	}
}

export default {
	createDisabledBrazeClient,
	createReadyBrazeClient,
	identifyBrazeUser,
	trackBrazeEvent,
	resetBrazeUser,
	isBrazePushSupported,
	requestBrazePushPermission
}
